import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '@/config/firebase';
import { CANAL_SI } from './publicacao.definitions';
import type {
  AnexoPublicacao,
  ArquivoDocumento,
  AutorPublicacao,
  DocumentoEditavel,
  VersaoDocumento,
  ComentarioPublicacao,
  MeuEstadoPublicacao,
  SeloComentario,
  Publicacao,
  PublicacaoEditavel,
  RegistroPessoaPublicacao,
} from './publicacao.types';

const COLECAO = 'si_publicacoes';

function semUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function paraPublicacao(id: string, data: Record<string, unknown>): Publicacao {
  return { id, ...data } as Publicacao;
}

/** Pessoa logada, gravada nas leituras/ciências/curtidas (doc id = e-mail de login). */
export interface PessoaLogada {
  email: string;
  colaboradorId: string | null;
  nome: string;
  avatar: string | null;
}

/**
 * Publicações do Portal do SI (posts; documentos entram na Fase 4). Regras em firestore.rules
 * (match /si_publicacoes): quem não tem publicador só lê as publicadas; escrever é só pra
 * publicador; leituras/ciências/curtidas só a própria pessoa; contadores só o servidor.
 */
export const publicacaoService = {
  /** Feed do Portal do SI: só as publicadas do canal SI (status é o que a regra exige pra listar). */
  async listarPublicadas(): Promise<Publicacao[]> {
    const snap = await getDocs(
      query(collection(db, COLECAO), where('status', '==', 'publicada'), where('canal', '==', CANAL_SI))
    );
    return snap.docs.map((d) => paraPublicacao(d.id, d.data()));
  },

  /**
   * Aba Publicações do Portal do SI: todas do canal SI, inclusive rascunhos e agendadas. Quem
   * publica lista tudo e marca como SI as antigas sem canal (criadas antes do canal existir); o
   * Administrar SI só pode listar filtrando canal == 'si' (firestore.rules). Ordenação no app
   * (canal + orderBy pediria índice composto).
   */
  async listarTodas(podePublicar: boolean): Promise<Publicacao[]> {
    const ordenar = (lista: Publicacao[]) =>
      lista.sort((a, b) => (b.atualizadoEm?.toMillis?.() ?? 0) - (a.atualizadoEm?.toMillis?.() ?? 0));
    if (!podePublicar) {
      const snap = await getDocs(query(collection(db, COLECAO), where('canal', '==', CANAL_SI)));
      return ordenar(snap.docs.map((d) => paraPublicacao(d.id, d.data())));
    }
    const snap = await getDocs(collection(db, COLECAO));
    const semCanal = snap.docs.filter((d) => !d.data().canal);
    for (let i = 0; i < semCanal.length; i += 400) {
      const lote = writeBatch(db);
      semCanal.slice(i, i + 400).forEach((d) => lote.update(d.ref, { canal: CANAL_SI }));
      await lote.commit();
    }
    return ordenar(
      snap.docs
        .map((d) => paraPublicacao(d.id, { ...d.data(), canal: d.data().canal ?? CANAL_SI }))
        .filter((p) => p.canal === CANAL_SI)
    );
  },

  async obter(id: string): Promise<Publicacao | null> {
    const snap = await getDoc(doc(db, COLECAO, id));
    return snap.exists() ? paraPublicacao(snap.id, snap.data()) : null;
  },

  /** Id gerado antes de salvar — os anexos sobem na hora em que são escolhidos (precisam do id). */
  novoId(): string {
    return doc(collection(db, COLECAO)).id;
  },

  async criar(dados: PublicacaoEditavel, autor: AutorPublicacao, id?: string): Promise<string> {
    const destino = doc(db, COLECAO, id ?? this.novoId());
    await setDoc(
      destino,
      semUndefined({
        ...dados,
        tipo: 'post',
        canal: CANAL_SI,
        status: 'rascunho',
        publicarEm: null,
        ciencia: { ...dados.ciencia, versao: 1 },
        autor,
        atualizadoPor: autor,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      })
    );
    return destino.id;
  },

  async salvar(id: string, dados: PublicacaoEditavel, autor: AutorPublicacao): Promise<void> {
    // A versão da ciência não muda ao editar (decisão: continua valendo) — só via pedirNovaCiencia.
    const { ciencia, ...resto } = dados;
    await updateDoc(
      doc(db, COLECAO, id),
      semUndefined({
        ...resto,
        'ciencia.exige': ciencia.exige,
        'ciencia.publico': ciencia.publico,
        'ciencia.projetoIds': ciencia.projetoIds,
        'ciencia.alvoIds': ciencia.alvoIds,
        'ciencia.prazo': ciencia.prazo,
        atualizadoPor: autor,
        atualizadoEm: serverTimestamp(),
      })
    );
  },

  /** `quando` null = agora. */
  async publicar(id: string, quando: Date | null, autor: AutorPublicacao): Promise<void> {
    const momento = quando ? Timestamp.fromDate(quando) : serverTimestamp();
    await updateDoc(doc(db, COLECAO, id), {
      status: quando ? 'agendada' : 'publicada',
      publicarEm: momento,
      anunciadoEm: momento,
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  async voltarParaRascunho(id: string, autor: AutorPublicacao): Promise<void> {
    await updateDoc(doc(db, COLECAO, id), {
      status: 'rascunho',
      publicarEm: null,
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  async despublicar(id: string, autor: AutorPublicacao): Promise<void> {
    await updateDoc(doc(db, COLECAO, id), { status: 'despublicada', atualizadoPor: autor, atualizadoEm: serverTimestamp() });
  },

  async pedirNovaCiencia(id: string, autor: AutorPublicacao): Promise<void> {
    await updateDoc(doc(db, COLECAO, id), {
      'ciencia.versao': increment(1),
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  async fixar(id: string, ate: Date | null, autor: AutorPublicacao): Promise<void> {
    await updateDoc(doc(db, COLECAO, id), {
      fixada: { ativa: true, ate: ate ? Timestamp.fromDate(ate) : null },
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  async desafixar(id: string, autor: AutorPublicacao): Promise<void> {
    await updateDoc(doc(db, COLECAO, id), {
      fixada: { ativa: false, ate: null },
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  /** Qualquer status: apaga doc, subcoleções e arquivos (function excluirPublicacaoSi). */
  async excluir(publicacaoId: string): Promise<void> {
    const fn = httpsCallable<{ publicacaoId: string }, { ok: boolean }>(getFunctions(), 'excluirPublicacaoSi');
    await fn({ publicacaoId });
  },

  /** `aoProgredir` recebe 0–100 (barra de envio). */
  async enviarArquivo(
    publicacaoId: string,
    arquivo: File,
    pasta: 'capa' | 'anexos' | 'documento',
    aoProgredir?: (percentual: number) => void
  ): Promise<AnexoPublicacao> {
    const nomeSeguro = arquivo.name.replace(/[^\w.-]+/g, '_');
    const path = `${COLECAO}/${publicacaoId}/${pasta}/${Date.now()}_${nomeSeguro}`;
    const arquivoRef = ref(storage, path);
    const tarefa = uploadBytesResumable(arquivoRef, arquivo, { contentType: arquivo.type });
    await new Promise<void>((resolve, reject) => {
      tarefa.on(
        'state_changed',
        (snap) => aoProgredir?.(snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0),
        reject,
        () => resolve()
      );
    });
    return { nome: arquivo.name, url: await getDownloadURL(arquivoRef), path, contentType: arquivo.type };
  },

  async removerArquivo(path: string): Promise<void> {
    await deleteObject(ref(storage, path)).catch(() => undefined);
  },

  // ---- Documentos (aba Documentos) ----

  async criarDocumento(dados: DocumentoEditavel, autor: AutorPublicacao, id?: string): Promise<string> {
    const destino = doc(db, COLECAO, id ?? this.novoId());
    await setDoc(
      destino,
      semUndefined({
        ...dados,
        tipo: 'documento',
        canal: CANAL_SI,
        status: 'rascunho',
        publicarEm: null,
        anunciadoEm: null,
        versao: 1,
        oQueMudou: null,
        capaUrl: null,
        capaPath: null,
        conteudo: '',
        fixada: { ativa: false, ate: null },
        ciencia: { ...dados.ciencia, versao: 1 },
        autor,
        atualizadoPor: autor,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      })
    );
    return destino.id;
  },

  async salvarDocumento(id: string, dados: DocumentoEditavel, autor: AutorPublicacao): Promise<void> {
    const { ciencia, ...resto } = dados;
    await updateDoc(doc(db, COLECAO, id), {
      ...resto,
      'ciencia.exige': ciencia.exige,
      'ciencia.publico': ciencia.publico,
      'ciencia.projetoIds': ciencia.projetoIds,
      'ciencia.alvoIds': ciencia.alvoIds,
      'ciencia.prazo': ciencia.prazo,
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
  },

  /**
   * Nova versão: guarda a atual em versoes/{n} (só quem publica vê) e troca o arquivo. Com
   * `pedirNovaCiencia`, a ciência dada na versão anterior deixa de valer; com `anunciar`, o
   * documento volta pro topo do feed da Início.
   */
  async novaVersao(
    documento: Publicacao,
    novo: { arquivo: ArquivoDocumento; oQueMudou: string; pedirNovaCiencia: boolean; anunciar: boolean },
    autor: AutorPublicacao
  ): Promise<void> {
    const atual = documento.versao ?? 1;
    const lote = writeBatch(db);
    if (documento.arquivo) {
      lote.set(doc(db, COLECAO, documento.id, 'versoes', String(atual)), {
        versao: atual,
        arquivo: documento.arquivo,
        oQueMudou: documento.oQueMudou ?? null,
        publicadaEm: documento.anunciadoEm ?? documento.publicarEm ?? null,
        autor: documento.atualizadoPor ?? documento.autor ?? null,
      });
    }
    lote.update(doc(db, COLECAO, documento.id), {
      arquivo: novo.arquivo,
      versao: atual + 1,
      oQueMudou: novo.oQueMudou.trim(),
      ...(novo.pedirNovaCiencia && documento.ciencia?.exige ? { 'ciencia.versao': increment(1) } : {}),
      ...(novo.anunciar ? { anunciarNoInicio: true, anunciadoEm: serverTimestamp() } : {}),
      atualizadoPor: autor,
      atualizadoEm: serverTimestamp(),
    });
    await lote.commit();
  },

  async listarVersoes(id: string): Promise<VersaoDocumento[]> {
    const snap = await getDocs(query(collection(db, COLECAO, id, 'versoes'), orderBy('versao', 'desc')));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as VersaoDocumento);
  },

  // ---- O que a pessoa faz (doc id = e-mail de login) ----

  async meuEstado(publicacaoId: string, email: string): Promise<MeuEstadoPublicacao> {
    const base = doc(db, COLECAO, publicacaoId);
    const [leitura, ciencia, curtida] = await Promise.all([
      getDoc(doc(base, 'leituras', email)),
      getDoc(doc(base, 'ciencias', email)),
      getDoc(doc(base, 'curtidas', email)),
    ]);
    return {
      lida: leitura.exists(),
      cienciaVersao: ciencia.exists() ? ((ciencia.data().versao as number | undefined) ?? 1) : null,
      curtiu: curtida.exists(),
    };
  },

  async marcarLida(publicacaoId: string, pessoa: PessoaLogada): Promise<void> {
    const leituraRef = doc(db, COLECAO, publicacaoId, 'leituras', pessoa.email);
    if ((await getDoc(leituraRef)).exists()) return;
    await setDoc(leituraRef, { colaboradorId: pessoa.colaboradorId, nome: pessoa.nome, em: serverTimestamp() });
  },

  async confirmarCiencia(publicacaoId: string, versao: number, pessoa: PessoaLogada): Promise<void> {
    await setDoc(doc(db, COLECAO, publicacaoId, 'ciencias', pessoa.email), {
      colaboradorId: pessoa.colaboradorId,
      nome: pessoa.nome,
      versao,
      em: serverTimestamp(),
    });
  },

  async curtir(publicacaoId: string, pessoa: PessoaLogada, curtir: boolean): Promise<void> {
    const curtidaRef = doc(db, COLECAO, publicacaoId, 'curtidas', pessoa.email);
    if (curtir) await setDoc(curtidaRef, { em: serverTimestamp() });
    else await deleteDoc(curtidaRef);
  },

  // ---- Comentários ----

  async listarComentarios(publicacaoId: string): Promise<ComentarioPublicacao[]> {
    const snap = await getDocs(query(collection(db, COLECAO, publicacaoId, 'comentarios'), orderBy('criadoEm', 'asc')));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ComentarioPublicacao);
  },

  async comentar(
    publicacaoId: string,
    texto: string,
    autor: AutorPublicacao,
    autorEmail: string,
    autorSelo: SeloComentario | null
  ): Promise<void> {
    await addDoc(collection(db, COLECAO, publicacaoId, 'comentarios'), {
      texto: texto.trim(),
      autor: semUndefined({ id: autor.id, name: autor.name, avatar: autor.avatar ?? null }),
      autorEmail,
      autorSelo,
      criadoEm: serverTimestamp(),
    });
  },

  async editarComentario(publicacaoId: string, comentarioId: string, texto: string): Promise<void> {
    await updateDoc(doc(db, COLECAO, publicacaoId, 'comentarios', comentarioId), {
      texto: texto.trim(),
      editadoEm: serverTimestamp(),
    });
  },

  async excluirComentario(publicacaoId: string, comentarioId: string): Promise<void> {
    await deleteDoc(doc(db, COLECAO, publicacaoId, 'comentarios', comentarioId));
  },

  // ---- Relatório (publicador) ----

  async registros(publicacaoId: string, sub: 'leituras' | 'ciencias'): Promise<RegistroPessoaPublicacao[]> {
    const snap = await getDocs(collection(db, COLECAO, publicacaoId, sub));
    return snap.docs.map((d) => ({ email: d.id, ...(d.data() as Omit<RegistroPessoaPublicacao, 'email'>) }));
  },

  async contarCiencias(publicacaoId: string, versao: number): Promise<number> {
    const snap = await getCountFromServer(
      query(collection(db, COLECAO, publicacaoId, 'ciencias'), where('versao', '>=', versao))
    );
    return snap.data().count;
  },

  /** Colaboradores com alocação ativa nos projetos escolhidos (resolvido no servidor). */
  async resolverPublicoPorProjeto(projetoIds: string[]): Promise<Array<{ id: string; name: string }>> {
    const fn = httpsCallable<{ projetoIds: string[] }, { colaboradores: Array<{ id: string; name: string }> }>(
      getFunctions(),
      'resolverPublicoCienciaPorProjeto'
    );
    const result = await fn({ projetoIds });
    return result.data.colaboradores;
  },
};
